import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Avatar,
  useTheme,
  Skeleton,
  alpha,
} from "@mui/material";
import {
  PaymentsOutlined,
  ShoppingCartOutlined,
  GroupOutlined,
  Inventory2Outlined,
  PendingActionsOutlined,
  WarningAmberOutlined,
  TrendingUpOutlined,
  TodayOutlined,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  useGetDashboardQuery,
  useGetSalesQuery,
  useGetCategoryAnalyticsQuery,
} from "state/api";
import { PageContainer, StatusChip, formatCurrency, formatDate } from "components/shared";

const KpiCard = ({ label, value, icon, color, loading }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Avatar sx={{ bgcolor: alpha(color, 0.15), color, width: 48, height: 48 }}>{icon}</Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={80} height={28} />
        ) : (
          <Typography variant="h4" fontWeight={700} noWrap>
            {value}
          </Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, children, height = 300 }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="h6" fontWeight={700} mb={2}>
        {title}
      </Typography>
      <Box sx={{ height }}>{children}</Box>
    </CardContent>
  </Card>
);

const EmptyChart = () => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "text.secondary" }}>
    <Typography variant="body2">No data yet</Typography>
  </Box>
);

const Dashboard = () => {
  const theme = useTheme();
  const p = theme.palette;
  const { data: dashData, isLoading } = useGetDashboardQuery();
  const { data: salesData } = useGetSalesQuery();
  const { data: catData } = useGetCategoryAnalyticsQuery();

  const s = dashData?.data || {};

  const kpis = [
    { label: "Total Revenue", value: formatCurrency(s.totalRevenue), icon: <PaymentsOutlined />, color: p.primary.main },
    { label: "This Month", value: formatCurrency(s.monthRevenue), icon: <TrendingUpOutlined />, color: p.secondary.main },
    { label: "Total Orders", value: s.totalOrders ?? 0, icon: <ShoppingCartOutlined />, color: p.info.main },
    { label: "Today's Orders", value: s.todayOrders ?? 0, icon: <TodayOutlined />, color: p.success.main },
    { label: "Customers", value: s.totalCustomers ?? 0, icon: <GroupOutlined />, color: p.primary.main },
    { label: "Products", value: s.totalProducts ?? 0, icon: <Inventory2Outlined />, color: p.info.main },
    { label: "Pending Payments", value: formatCurrency(s.pendingPayments), icon: <PendingActionsOutlined />, color: p.warning.main },
    { label: "Low Stock", value: s.lowStockCount ?? 0, icon: <WarningAmberOutlined />, color: p.error.main },
  ];

  const salesSeries = (salesData?.data?.salesByDay ?? []).map((d) => ({
    date: formatDate(d.date || d._id),
    revenue: d.totalRevenue || 0,
  }));
  const categorySeries = (catData?.data ?? []).map((c) => ({ category: c.category, revenue: c.revenue || 0 }));
  const ordersByStatus = s.ordersByStatus || {};
  const topProducts = salesData?.data?.topProducts ?? [];
  const axisStyle = { fontSize: 12, fill: p.text.secondary };
  const tooltipStyle = {
    backgroundColor: p.background.paper,
    border: `1px solid ${p.divider}`,
    borderRadius: 8,
    color: p.text.primary,
  };

  return (
    <PageContainer title="Dashboard" subtitle="Business performance at a glance">
      <Grid container spacing={2}>
        {kpis.map((k) => (
          <Grid item xs={12} sm={6} md={3} key={k.label}>
            <KpiCard {...k} loading={isLoading} />
          </Grid>
        ))}

        <Grid item xs={12} md={8}>
          <ChartCard title="Revenue Trend">
            {salesSeries.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={salesSeries}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={p.primary.main} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={p.primary.main} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={p.divider} />
                  <XAxis dataKey="date" tick={axisStyle} stroke={p.divider} minTickGap={24} />
                  <YAxis tick={axisStyle} stroke={p.divider} width={70} tickFormatter={(v) => formatCurrency(v).replace(/\.00$/, "")} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
                  <Area type="monotone" dataKey="revenue" stroke={p.primary.main} fill="url(#rev)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartCard title="Orders by Status">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
              {Object.keys(ordersByStatus).length === 0 ? (
                <EmptyChart />
              ) : (
                Object.entries(ordersByStatus).map(([status, count]) => (
                  <Box key={status} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <StatusChip status={status} />
                    <Typography fontWeight={700}>{count}</Typography>
                  </Box>
                ))
              )}
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <ChartCard title="Revenue by Category">
            {categorySeries.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categorySeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke={p.divider} vertical={false} />
                  <XAxis dataKey="category" tick={axisStyle} stroke={p.divider} />
                  <YAxis tick={axisStyle} stroke={p.divider} width={70} tickFormatter={(v) => formatCurrency(v).replace(/\.00$/, "")} />
                  <Tooltip cursor={{ fill: alpha(p.primary.main, 0.08) }} contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
                  <Bar dataKey="revenue" fill={p.primary.main} radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <ChartCard title="Top Products">
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {topProducts.length === 0 ? (
                <EmptyChart />
              ) : (
                topProducts.slice(0, 6).map((tp, i) => (
                  <Box
                    key={tp._id || tp.name || i}
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: `1px solid ${p.divider}` }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: alpha(p.primary.main, 0.15), color: p.primary.main }}>
                        {i + 1}
                      </Avatar>
                      <Typography noWrap>{tp.name || tp.productName || "Product"}</Typography>
                    </Box>
                    <Typography fontWeight={700} sx={{ whiteSpace: "nowrap", pl: 1 }}>
                      {formatCurrency(tp.revenue || tp.totalRevenue)}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </ChartCard>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default Dashboard;
